export const currentUser = {
  name: 'Sarah Mitchell',
  role: 'Loan Officer',
  department: 'Lending',
  initials: 'SM',
  email: 'sarah.mitchell@company.com',
}

export const departments = [
  'All',
  'Lending',
  'Processing',
  'Closing',
  'Underwriting',
  'IT',
  'Compliance',
  'HR',
]

export const categories = [
  'Software Access',
  'Hardware Issue',
  'Network/VPN',
  'Salesforce',
  'Encompass LOS',
  'Email/Calendar',
  'Printer',
  'Account/Password',
  'Other',
]

export const knowledgeBaseArticles = [
  {
    id: 'kb-1',
    title: 'Salesforce not loading loan records',
    summary:
      'Troubleshooting steps when Salesforce dashboards or individual loan records fail to load or timeout.',
    category: 'Salesforce',
    department: 'Lending',
    tags: ['salesforce', 'timeout', 'loan records'],
    views: 1245,
    helpful: 98,
    updatedAt: '2024-02-15',
  },
  {
    id: 'kb-2',
    title: 'VPN disconnects during remote closings',
    summary:
      'How to stabilize your GlobalProtect VPN connection when working with clients remotely.',
    category: 'Network/VPN',
    department: 'Closing',
    tags: ['vpn', 'remote', 'connection'],
    views: 856,
    helpful: 85,
    updatedAt: '2024-03-01',
  },
  {
    id: 'kb-3',
    title: 'Encompass LOS sync issues',
    summary:
      'Resolve common synchronization errors between Encompass and our internal pricing engine.',
    category: 'Encompass LOS',
    department: 'Processing',
    tags: ['encompass', 'sync', 'pricing'],
    views: 2104,
    helpful: 92,
    updatedAt: '2024-01-20',
  },
  {
    id: 'kb-4',
    title: 'How to reset your Salesforce password',
    summary:
      'Step-by-step guide to securely resetting your Salesforce CRM password without IT intervention.',
    category: 'Account/Password',
    department: 'All',
    tags: ['password', 'salesforce', 'login'],
    views: 3420,
    helpful: 99,
    updatedAt: '2024-03-10',
  },
  {
    id: 'kb-5',
    title: 'Setting up email signatures for compliance',
    summary:
      'Ensure your Outlook signature meets the latest CFPB and internal compliance requirements.',
    category: 'Email/Calendar',
    department: 'Compliance',
    tags: ['email', 'signature', 'compliance'],
    views: 645,
    helpful: 76,
    updatedAt: '2024-02-28',
  },
  {
    id: 'kb-6',
    title: 'Printer setup for loan document packages',
    summary:
      'Configure your local or branch printer for massive loan document packages (legal size support).',
    category: 'Printer',
    department: 'Closing',
    tags: ['printer', 'hardware', 'documents'],
    views: 432,
    helpful: 65,
    updatedAt: '2023-11-15',
  },
  {
    id: 'kb-7',
    title: 'Requesting access to Underwriting shared drives',
    summary:
      'The approval process and form required to access secure underwriting network drives.',
    category: 'Software Access',
    department: 'Underwriting',
    tags: ['access', 'network drive', 'security'],
    views: 320,
    helpful: 88,
    updatedAt: '2024-01-05',
  },
  {
    id: 'kb-8',
    title: 'Clear browser cache for pricing engine errors',
    summary:
      'Fix 90% of pricing engine display errors by clearing your Chrome or Edge browser cache.',
    category: 'Software Access',
    department: 'Lending',
    tags: ['cache', 'browser', 'pricing'],
    views: 1850,
    helpful: 95,
    updatedAt: '2024-03-12',
  },
]

export const userTickets = [
  {
    id: 'IT-2024-089',
    subject: 'Cannot access new loan file in Encompass',
    description:
      'I am trying to open the Smith file (Loan #12345) but getting an access denied error.',
    category: 'Encompass LOS',
    priority: 'High',
    status: 'In Progress',
    department: 'Lending',
    submittedDate: '2 hours ago',
    lastUpdate: 'Assigned to IT Support',
    assignedTo: 'Mike Johnson',
    progress: 50,
  },
  {
    id: 'IT-2024-072',
    subject: 'Need dual monitors for home office',
    description:
      'Requesting a second monitor to help with reviewing closing disclosures.',
    category: 'Hardware Issue',
    priority: 'Low',
    status: 'Waiting on User',
    department: 'Lending',
    submittedDate: '2 days ago',
    lastUpdate: 'Waiting for manager approval',
    assignedTo: 'Sarah Connor',
    progress: 30,
  },
  {
    id: 'IT-2024-045',
    subject: 'Salesforce dashboard not refreshing',
    description: 'My daily leads dashboard is stuck on last weeks data.',
    category: 'Salesforce',
    priority: 'Medium',
    status: 'Resolved',
    department: 'Lending',
    submittedDate: '1 week ago',
    lastUpdate: 'Issue resolved by clearing cache',
    assignedTo: 'Alex Chen',
    progress: 100,
  },
  {
    id: 'IT-2024-091',
    subject: 'VPN keeps dropping during client calls',
    description: 'GlobalProtect disconnects every 15 minutes.',
    category: 'Network/VPN',
    priority: 'Critical',
    status: 'New',
    department: 'Lending',
    submittedDate: '15 mins ago',
    lastUpdate: 'Ticket created',
    assignedTo: null,
    progress: 10,
  },
]

export const slaInfo = {
  firstResponse: '&lt; 2 hours',
  resolutionLow: '3 business days',
  resolutionMedium: '1 business day',
  resolutionHigh: '4 hours',
  resolutionCritical: '1 hour',
}

export const ticketComments: Record<string, any[]> = {
  'IT-2024-089': [
    {
      id: 'c-1',
      ticketId: 'IT-2024-089',
      author: 'System',
      authorInitials: 'SYS',
      authorRole: 'Automated',
      content: 'Ticket created and routed to Lending Support queue.',
      timestamp: '2 hours ago',
      isInternal: true,
    },
    {
      id: 'c-2',
      ticketId: 'IT-2024-089',
      author: 'Mike Johnson',
      authorInitials: 'MJ',
      authorRole: 'IT Support',
      content:
        'Hi Sarah, I am looking into this now. Can you confirm if you are getting a specific error code when trying to open the Smith file? A screenshot would be helpful.',
      timestamp: '1 hour ago',
      isInternal: false,
    },
    {
      id: 'c-3',
      ticketId: 'IT-2024-089',
      author: 'Sarah Mitchell',
      authorInitials: 'SM',
      authorRole: 'Loan Officer',
      content:
        '@Mike Johnson It just says "Access Denied - You do not have the required permissions to view this loan folder." No specific error code number.',
      timestamp: '45 mins ago',
      isInternal: false,
    },
  ],
  'IT-2024-072': [
    {
      id: 'c-4',
      ticketId: 'IT-2024-072',
      author: 'System',
      authorInitials: 'SYS',
      authorRole: 'Automated',
      content: 'Ticket created and routed to Hardware Requests queue.',
      timestamp: '2 days ago',
      isInternal: true,
    },
    {
      id: 'c-5',
      ticketId: 'IT-2024-072',
      author: 'Sarah Connor',
      authorInitials: 'SC',
      authorRole: 'IT Support',
      content:
        'Hello! For secondary monitor requests for home offices, we need a signed approval form from your branch manager. Please have them email the approval to IT-Hardware@company.com and we will get this shipped out.',
      timestamp: '1 day ago',
      isInternal: false,
    },
  ],
  'IT-2024-045': [
    {
      id: 'c-6',
      ticketId: 'IT-2024-045',
      author: 'System',
      authorInitials: 'SYS',
      authorRole: 'Automated',
      content: 'Ticket created and routed to Salesforce Support queue.',
      timestamp: '1 week ago',
      isInternal: true,
    },
    {
      id: 'c-7',
      ticketId: 'IT-2024-045',
      author: 'Alex Chen',
      authorInitials: 'AC',
      authorRole: 'IT Support',
      content:
        'Hi Sarah, there was a known issue with the data sync overnight. It has been resolved on the backend. Can you try clearing your browser cache and refreshing the dashboard?',
      timestamp: '6 days ago',
      isInternal: false,
    },
    {
      id: 'c-8',
      ticketId: 'IT-2024-045',
      author: 'Sarah Mitchell',
      authorInitials: 'SM',
      authorRole: 'Loan Officer',
      content:
        'That worked! The leads from yesterday are showing up now. Thank you.',
      timestamp: '6 days ago',
      isInternal: false,
    },
    {
      id: 'c-9',
      ticketId: 'IT-2024-045',
      author: 'Alex Chen',
      authorInitials: 'AC',
      authorRole: 'IT Support',
      content:
        'Great to hear. I will go ahead and close this ticket. Have a great week!',
      timestamp: '6 days ago',
      isInternal: false,
    },
  ],
  'IT-2024-091': [
    {
      id: 'c-10',
      ticketId: 'IT-2024-091',
      author: 'System',
      authorInitials: 'SYS',
      authorRole: 'Automated',
      content:
        'Ticket created and routed to Network/VPN Support queue. Priority set to Critical based on keywords.',
      timestamp: '15 mins ago',
      isInternal: true,
    },
  ],
}
